import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import CommonTable, { CommonTableProps } from "../../components/CommonTable";
import {
  addHotel,
  bulkInsertHotel,
  deleteHotel,
  updateHotel,
} from "redux/actions/hotel";
import HotelRes from "../../types/res/hotel/HotelRes";
import { FormType } from "components/FormField";
import { fetchArea } from "redux/actions/area";
import CreateHotelReq from "../../types/req/hotel/CreateHotelReq";
import UpdateHotelReq from "../../types/req/hotel/UpdateHotelReq";
import { Box, Button, TextField } from "@material-ui/core";
import CsvImport from "components/CsvImport";
import { CsvFieldType } from "utils/CsvUtils";
import HotelApi from "api/HotelApi";

const Hotel: React.FC = () => {
  const { t } = useTranslation("master");
  const dispatch = useDispatch();

  const companyId = useSelector((state) => state.account.staff.companyId);
  const areas = useSelector((state) => state.area);
  const [openCsvImport, setOpenCsvImport] = useState(false);

  const [hotels, setHotels] = useState<HotelRes[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [limit, setLimit] = useState(100);
  const [offset, setOffset] = useState(0);
  const [searchName, setSearchName] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const loadHotels = useCallback(
    async (opts: { limit: number; offset: number; name: string }) => {
      if (!companyId) return;
      setIsLoading(true);
      try {
        const [list, cnt] = await Promise.all([
          HotelApi.findAllWithPagination(companyId, {
            limit: opts.limit,
            offset: opts.offset,
            name: opts.name || undefined,
          }),
          HotelApi.count(companyId, { name: opts.name || undefined }),
        ]);
        setHotels(list);
        setTotalCount(cnt);
      } finally {
        setIsLoading(false);
      }
    },
    [companyId]
  );

  useEffect(() => {
    dispatch(fetchArea(companyId));
  }, [companyId, dispatch]);

  useEffect(() => {
    loadHotels({ limit, offset: offset * limit, name: searchName });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, limit, offset]);

  const onSearch = () => {
    setOffset(0);
    loadHotels({ limit, offset: 0, name: searchName });
  };

  const headers: { key: keyof HotelRes; label: string }[] = [
    { key: "name", label: t("hotel.headers.name") },
    { key: "kana", label: t("hotel.headers.kana") },
    { key: "address", label: t("hotel.headers.address") },
    { key: "tel", label: t("hotel.headers.tel") },
    { key: "totalFee", label: t("hotel.headers.totalFee") },
    { key: "shopFee", label: t("hotel.headers.shopFee") },
    { key: "castFee", label: t("hotel.headers.castFee") },
    { key: "isIncludeOrderPrice", label: t("hotel.headers.isIncludeOrderPrice") },
    { key: "memo", label: t("hotel.headers.memo") },
    { key: "sort", label: t("hotel.headers.sort") },
    { key: "isGuestShow", label: t("hotel.headers.isGuestShow") },
    { key: "areas", label: t("hotel.headers.areas") },
  ];

  const forms = [
    {
      label: t("hotel.forms.name"),
      key: "name",
      type: FormType.Text,
    },
    {
      label: t("hotel.forms.kana"),
      key: "kana",
      type: FormType.Text,
    },
    {
      label: t("hotel.forms.address"),
      key: "address",
      type: FormType.Text,
    },
    {
      label: t("hotel.forms.tel"),
      key: "tel",
      type: FormType.Text,
    },
    {
      label: t("hotel.forms.totalFee"),
      key: "totalFee",
      type: FormType.Number,
    },
    {
      label: t("hotel.forms.shopFee"),
      key: "shopFee",
      type: FormType.Number,
    },
    {
      label: t("hotel.forms.castFee"),
      key: "castFee",
      type: FormType.Number,
    },
    {
      label: t("hotel.forms.isIncludeOrderPrice"),
      key: "isIncludeOrderPrice",
      type: FormType.Boolean,
      options: [
        { id: "0", name: t("hotel.options.notIncluded") },
        { id: "1", name: t("hotel.options.included") },
      ],
    },
    {
      label: t("hotel.forms.memo"),
      key: "memo",
      type: FormType.Text,
    },
    {
      label: t("hotel.forms.sort"),
      key: "sort",
      type: FormType.Number,
    },
    {
      label: t("hotel.forms.isGuestShow"),
      key: "isGuestShow",
      type: FormType.Boolean,
      options: [
        { id: "0", name: t("hotel.options.hidden") },
        { id: "1", name: t("hotel.options.shown") },
      ],
    },
    {
      label: t("hotel.forms.areas"),
      key: "areas",
      type: FormType.AutoCompleteMultiOptions,
      options: areas?.map((s) => ({ id: s.areaId, name: s.name })) || [],
      value: (val: any) => val.map((a: any) => a.areaId),
    },
  ];

  const csvHeader = [
    {
      label: "名前",
      key: "name",
      type: CsvFieldType.Text,
    },
    {
      label: "読みがな",
      key: "kana",
      type: CsvFieldType.Text,
    },
    {
      label: "住所",
      key: "address",
      type: CsvFieldType.Text,
    },
    {
      label: "電話番号",
      key: "tel",
      type: CsvFieldType.Text,
    },
    {
      label: "ホテル料金",
      key: "totalFee",
      type: CsvFieldType.Number,
    },
    {
      label: "店舗料金",
      key: "shopFee",
      type: CsvFieldType.Number,
    },
    {
      label: "キャスト料金",
      key: "castFee",
      type: CsvFieldType.Number,
    },
    {
      label: "予約料金に含む",
      key: "isIncludeOrderPrice",
      type: CsvFieldType.Boolean,
    },
    {
      label: "備考",
      key: "memo",
      type: CsvFieldType.Text,
    },
    {
      label: "並び順",
      key: "sort",
      type: CsvFieldType.Number,
    },
    {
      label: "顧客モバイル表示",
      key: "isGuestShow",
      type: CsvFieldType.Boolean,
    },

    {
      label: "紐付けエリア",
      key: "areas",
      type: CsvFieldType.Array,
      relation: areas,
      relationKey: "name",
      relationId: "areaId",
    },
  ];

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        marginTop={1}
        marginX={2}
        flexWrap="wrap"
      >
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <TextField
            label={t("hotel.search.nameLabel")}
            size="small"
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onSearch();
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={onSearch}
            disabled={isLoading}
          >
            {t("hotel.search.searchButton")}
          </Button>
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpenCsvImport((prev) => !prev)}
        >
          {t("hotel.csvImport")}
        </Button>
      </Box>
      {openCsvImport && (
        <CsvImport
          templatePath={"/csv/hotel.csv"}
          templateName={"hotel_template.csv"}
          csvHeader={csvHeader}
          addType={CreateHotelReq}
          addFunc={(formData) => bulkInsertHotel(companyId, formData)}
        />
      )}
      <HotelTable
        title={t("hotel.title")}
        formId="hotelForm"
        forms={forms}
        rows={headers}
        data={hotels}
        addType={CreateHotelReq}
        updateType={UpdateHotelReq}
        isLoading={isLoading}
        totalCount={totalCount}
        limit={limit}
        offset={offset}
        setLimit={setLimit}
        setOffset={(v: any) => setOffset(Number(v))}
        addFunc={(formData: any) => async (d: any) => {
          const r = await addHotel(companyId, formData)(d);
          await loadHotels({
            limit,
            offset: offset * limit,
            name: searchName,
          });
          return r;
        }}
        updateFunc={(formData: any) => async (d: any) => {
          const r = await updateHotel(companyId, formData)(d);
          await loadHotels({
            limit,
            offset: offset * limit,
            name: searchName,
          });
          return r;
        }}
        deleteFunc={(item: any) => async (d: any) => {
          const r = await deleteHotel(companyId, {
            hotelId: item.hotelId,
          })(d);
          await loadHotels({
            limit,
            offset: offset * limit,
            name: searchName,
          });
          return r;
        }}
        values={[
          (s) => s.name,
          (s) => s.kana,
          (s) => s.address || t("hotel.values.unset"),
          (s) => s.tel || t("hotel.values.unset"),
          (s) => s.totalFee || t("hotel.values.unset"),
          (s) => s.shopFee || t("hotel.values.unset"),
          (s) => s.castFee || t("hotel.values.unset"),
          (s) => (s.isIncludeOrderPrice ? t("hotel.values.included") : t("hotel.values.notIncluded")),
          (s) => s.memo || "",
          (s) => s.sort || t("hotel.values.unset"),
          (s) => (s.isGuestShow ? t("hotel.values.shown") : t("hotel.values.hidden")),
          (s) => s.areas?.map((area) => area.name).join(",") || t("hotel.values.noArea"),
        ]}
      />
    </>
  );
};

const HotelTable = styled<
  React.FC<CommonTableProps<HotelRes, CreateHotelReq, UpdateHotelReq>>
>(CommonTable)`
  margin-top: 24px;
`;

export default Hotel;
